<template>
  <div class="clock-page" :class="{ 'fullscreen-mode': isFullscreen }">
    <div class="clock-controls" v-if="!isFullscreen">
      <button :class="['ctrl-btn', { active: clockMode === 'analog' }]" @click="clockMode = 'analog'">模拟</button>
      <button :class="['ctrl-btn', { active: clockMode === 'digital' }]" @click="clockMode = 'digital'">数字</button>
      <button :class="['ctrl-btn', { active: clockMode === 'both' }]" @click="clockMode = 'both'">双显</button>
      <button class="ctrl-btn" @click="isDark = !isDark">{{ isDark ? '浅色' : '深色' }}</button>
      <button class="ctrl-btn" @click="toggleFullscreen">⛶ 全屏</button>
    </div>

    <div class="clock-main" :class="{ 'dark-theme': isDark }">
      <!-- 模拟时钟 -->
      <div v-if="clockMode === 'analog' || clockMode === 'both'" class="clock-segment">
        <div class="analog-clock">
          <svg viewBox="0 0 300 300" class="analog-svg">
            <circle cx="150" cy="150" r="145" fill="none" :stroke="isDark ? '#444' : '#f0f0f0'" stroke-width="3" />
            <!-- 刻度 -->
            <line v-for="n in 60" :key="'t'+n"
              :x1="150 + (n % 5 === 0 ? 116 : 124) * Math.cos(((n * 6 - 90) * Math.PI) / 180)"
              :y1="150 + (n % 5 === 0 ? 116 : 124) * Math.sin(((n * 6 - 90) * Math.PI) / 180)"
              :x2="150 + 135 * Math.cos(((n * 6 - 90) * Math.PI) / 180)"
              :y2="150 + 135 * Math.sin(((n * 6 - 90) * Math.PI) / 180)"
              :stroke="n % 5 === 0 ? (isDark ? '#ccc' : '#333') : (isDark ? '#555' : '#ddd')"
              :stroke-width="n % 5 === 0 ? 2.5 : 1" stroke-linecap="round"
            />
            <!-- 数字 -->
            <text v-for="n in 12" :key="'num'+n"
              :x="150 + 98 * Math.cos(((n * 30 - 90) * Math.PI) / 180)"
              :y="150 + 98 * Math.sin(((n * 30 - 90) * Math.PI) / 180)"
              text-anchor="middle" dominant-baseline="central"
              :font-size="20" font-weight="600" :fill="isDark ? '#ddd' : '#333'"
            >{{ n }}</text>
            <!-- 时针 -->
            <line x1="150" y1="150"
              :x2="150 + 70 * Math.cos(((hourAngle - 90) * Math.PI) / 180)"
              :y2="150 + 70 * Math.sin(((hourAngle - 90) * Math.PI) / 180)"
              :stroke="isDark ? '#fff' : '#1a1a2e'" stroke-width="5" stroke-linecap="round"
            />
            <!-- 分针 -->
            <line x1="150" y1="150"
              :x2="150 + 100 * Math.cos(((minuteAngle - 90) * Math.PI) / 180)"
              :y2="150 + 100 * Math.sin(((minuteAngle - 90) * Math.PI) / 180)"
              :stroke="isDark ? '#ddd' : '#444'" stroke-width="3.5" stroke-linecap="round"
            />
            <!-- 秒针 -->
            <line x1="150" y1="150"
              :x2="150 + 95 * Math.cos(((secondAngle - 90) * Math.PI) / 180)"
              :y2="150 + 95 * Math.sin(((secondAngle - 90) * Math.PI) / 180)"
              stroke="#e74c3c" stroke-width="1.5" stroke-linecap="round"
            />
            <circle cx="150" cy="150" r="6" fill="#e74c3c" />
            <circle cx="150" cy="150" r="2.5" :fill="isDark ? '#1a1a2e' : '#fff'" />
          </svg>
        </div>
      </div>

      <!-- 数字时钟 -->
      <div v-if="clockMode === 'digital' || clockMode === 'both'" class="clock-segment">
        <div class="digital-time">{{ timeStr }}</div>
        <div class="digital-date">{{ dateStr }}</div>
        <div class="digital-week">{{ weekStr }} · 第{{ weekNum }}周</div>
      </div>
    </div>

    <!-- 底部提示 -->
    <div class="clock-tip" v-if="!isFullscreen">按 F11 可进入浏览器全屏模式</div>
  </div>
</template>

<script>
export default {
  name: 'ClockPage',
  data() {
    return {
      clockMode: 'both',
      isDark: false,
      isFullscreen: false,
      hourAngle: 0,
      minuteAngle: 0,
      secondAngle: 0,
      timeStr: '',
      dateStr: '',
      weekStr: '',
      weekNum: 0,
      timer: null,
      lastSecond: -1
    }
  },
  mounted() {
    this.updateAll()
    this.timer = setInterval(this.updateAll, 50)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
  },
  methods: {
    updateAll() {
      const now = new Date()
      const h = now.getHours()
      const m = now.getMinutes()
      const s = now.getSeconds()
      const ms = now.getMilliseconds()

      this.hourAngle = ((h % 12) + m / 60) * 30
      this.minuteAngle = (m + s / 60) * 6
      this.secondAngle = (s + ms / 1000) * 6

      if (s !== this.lastSecond) {
        this.lastSecond = s
        const pad = (n) => String(n).padStart(2, '0')
        this.timeStr = `${pad(h)}:${pad(m)}:${pad(s)}`
        this.dateStr = `${now.getFullYear()}年${pad(now.getMonth()+1)}月${pad(now.getDate())}日`
        const days = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
        this.weekStr = days[now.getDay()]
        this.weekNum = this.getWeekNumber(now)
      }
    },
    getWeekNumber(d) {
      const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
      const dayNum = date.getUTCDay() || 7
      date.setUTCDate(date.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
      return Math.ceil(((date - yearStart) / 86400000 + 1) / 7)
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen()
      } else {
        document.exitFullscreen()
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
    }
  }
}
</script>

<style scoped>
.clock-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 16px 40px;
  min-height: 70vh;
}

.clock-controls {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}
.ctrl-btn {
  padding: 7px 16px;
  font-size: 13px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}
.ctrl-btn:hover { background: #f0f0f0; border-color: #bbb; }
.ctrl-btn.active { background: #1677ff; color: #fff; border-color: #1677ff; }

.clock-main {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;
  padding: 40px 20px;
  border-radius: 20px;
  transition: background 0.3s;
}
.clock-main.dark-theme {
  background: #1a1a2e;
}

.clock-segment {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 模拟时钟 */
.analog-svg { width: 280px; height: 280px; }

/* 数字时钟 */
.digital-time {
  font-size: 80px;
  font-weight: 300;
  font-family: 'SF Mono', 'Consolas', 'Menlo', 'Courier New', monospace;
  font-feature-settings: 'tnum';
  letter-spacing: 4px;
  color: #1a1a2e;
}
.dark-theme .digital-time { color: #fff; }
.digital-date {
  font-size: 20px;
  color: #888;
  margin-top: 8px;
}
.dark-theme .digital-date { color: #aaa; }
.digital-week {
  font-size: 15px;
  color: #aaa;
  margin-top: 4px;
}
.dark-theme .digital-week { color: #777; }

.clock-tip {
  text-align: center;
  margin-top: 30px;
  font-size: 12px;
  color: #ccc;
}

@media (max-width: 600px) {
  .digital-time { font-size: 50px; }
  .analog-svg { width: 220px; height: 220px; }
  .clock-main { padding: 24px 12px; gap: 20px; }
}

/* 全屏模式 */
.clock-page.fullscreen-mode {
  max-width: 100vw;
  height: 100vh;
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.clock-page.fullscreen-mode .clock-main {
  flex: 0 0 auto;
}
</style>

<style>
/* 全屏时隐藏全局头部和底部 */
.clock-page.fullscreen-mode ~ .site-footer,
body:has(.clock-page.fullscreen-mode) .top-nav,
body:has(.clock-page.fullscreen-mode) .site-footer {
  display: none;
}
</style>
